import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { token } from '@bp-files/ui';
import { VIEWER_COPY } from './viewer.copy';

/**
 * The full-screen frame both viewers are shown inside (FIL-8a, FIL-8b).
 *
 * A header carrying the file's name and the acts that apply to it, a footer the
 * caller fills with whatever "where am I" means for what it is showing, and the
 * content filling everything between. Nothing else — this is a reader, and what
 * is being read is the product.
 *
 * **The chrome does not auto-hide, and that is a decision.** The reference
 * implementation fades its controls after 2.5 seconds of stillness, and the
 * three interacting effects that took — plus a `mousemove`-not-`pointermove`
 * workaround and a "this must not be a dependency" warning — each document a
 * bug the feature caused. It is also wrong here for a reason beyond cost: in a
 * gallery the interface is an interruption, but a filename on an evidence photo
 * is part of the record being read, and controls that vanish on stillness are a
 * real barrier for anyone who needs time to aim at them.
 *
 * **It borrows `DetailDrawer`'s mechanism without borrowing `DetailDrawer`.**
 * That component is a right-edge `max-w-md` slide-over with a heading row and
 * an actions footer; a full-bleed viewer is a different composition, and giving
 * the shared one a `variant="fullscreen"` would leave every other caller
 * carrying a mode none of them wants. What is copied deliberately is the
 * portal, the focusable-selector list, the Tab trap, and the focus restore —
 * and the portal is load-bearing rather than stylistic: `bp-animate-fade-in` on
 * a surface wrapper creates a stacking context, so a `z-50` inside one is not
 * 50. Read that component's own header before changing any of it. Extracting
 * the trap into a shared hook is the right eventual move and is its own slice.
 *
 * **Escape is handled here; every other key belongs to the caller.** A viewer's
 * keys are about what it is showing — arrows step photographs in one and scroll
 * pages in the other — and a frame that guessed would have to be told which
 * kind it was wrapping. Closing is the one act that means the same thing in
 * both, so it is the one this owns.
 */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export const FRAME_BUTTON_CLASS =
  'flex min-h-9 min-w-9 items-center justify-center rounded-md p-2 transition-colors hover:bg-elevated disabled:pointer-events-none disabled:opacity-40';

/**
 * The same button at a thumb's size rather than a cursor's.
 *
 * 36px is a fine target for a pointer and under every touch guideline there is
 * — WCAG 2.5.5 asks 44, and so does iOS. `FRAME_BUTTON_CLASS` is deliberately
 * not widened to match: it is every control in a desktop toolbar and both of
 * `ImageViewer`'s footer arrows, and growing all of them to solve a phone's
 * problem would spend horizontal space on the screen that has least of it.
 */
export const FRAME_TOUCH_BUTTON_CLASS =
  'flex min-h-11 min-w-11 items-center justify-center rounded-md p-2 transition-colors hover:bg-elevated disabled:pointer-events-none disabled:opacity-40';

export interface ViewerFrameProps {
  /** Names the dialog. The file's name, in every real case. */
  title: string;
  /** Ties `aria-labelledby` to the heading. The caller owns it so it can reuse it. */
  titleId: string;
  /** Controls sitting between the title and the overflow — zoom, paging, tools. */
  toolbar?: ReactNode;
  /** Items for the overflow menu. Rendered only when there are some. */
  menuItems?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  onClose: () => void;
  /**
   * Which end of the header the close control sits at. `end` by default, which
   * is every desktop reader and both of `ImageViewer`'s breakpoints.
   *
   * `start` is the phone's reading order — leave, then what you are looking at,
   * then the overflow — and matches every mobile document view a reader has
   * already met. It is the *same button* either way: one node, moved, never
   * two, so there is never a second control answering to "Close".
   */
  closePlacement?: 'start' | 'end';
  /**
   * Lets the caller take Escape first, for a mode of its own to leave.
   *
   * Returning `true` means "handled, do not close" — the shape `ImageViewer`'s
   * own overflow menu already needed, and the shape a sign-mode tool will need
   * for the same reason: one press should back out of the innermost thing, not
   * the whole reader.
   */
  onEscape?: () => boolean;
}

export function ViewerFrame({
  title,
  titleId,
  toolbar,
  menuItems,
  footer,
  children,
  onClose,
  closePlacement = 'end',
  onEscape,
}: ViewerFrameProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const focusables = useCallback((): HTMLElement[] => {
    const dialog = dialogRef.current;
    if (!dialog) return [];

    // No `offsetParent` filter, for `DetailDrawer`'s reason: inside a fixed
    // container it is null in real browsers as well as jsdom, and would
    // discard every candidate.
    return Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
  }, []);

  /**
   * Focus the dialog itself rather than the first control.
   *
   * Landing on the first button would announce a control before the thing it
   * belongs to, and would make Enter act on the file — a keystroke away from
   * losing what the reader just opened.
   */
  useEffect(() => {
    restoreFocusTo.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    return () => {
      // Optional-chained on purpose: a viewer reached by pasting `?view=` into
      // the address bar was opened by nothing, and has nowhere to put focus
      // back.
      restoreFocusTo.current?.focus();
    };
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        if (menuOpen) {
          setMenuOpen(false);
          return;
        }
        if (onEscape?.() === true) return;
        onClose();
        return;
      }

      if (event.key === 'Tab') {
        const elements = focusables();
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (!first || !last) return;

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [focusables, menuOpen, onClose, onEscape]);

  useEffect(
    () => () => {
      clearTimeout(blurTimer.current);
    },
    [],
  );

  /*
    One node, rendered from one of two places. Building it here rather than
    writing the button twice is the whole safety of `closePlacement`: two
    copies behind a ternary is two things that can drift, and — worse — a
    refactor that ever renders both leaves two controls answering to "Close",
    which is exactly the ambiguity every `getByRole('button', { name })` in the
    viewer tests would break on.
  */
  const closeButton = (
    <button
      type="button"
      onClick={onClose}
      aria-label={VIEWER_COPY.close}
      className={`${FRAME_BUTTON_CLASS} text-secondary hover:text-body`}
    >
      <CloseIcon />
    </button>
  );

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col outline-none"
      /*
        Opaque `bp-app`, not `bp-scrim`. This replaces the page rather than
        veiling it — and a translucent surface *above* the content would force
        an offscreen buffer, which is the one thing `ZoomStage`'s own comment
        forbids and which softens a re-rendered PDF page just as badly.
      */
      style={{ background: token('bp-app'), color: token('bp-text') }}
    >
      <header
        className="flex shrink-0 items-center justify-between gap-3 border-b px-4 py-3"
        style={{ borderColor: token('bp-border'), background: token('bp-panel') }}
      >
        {closePlacement === 'start' ? closeButton : null}

        <h2 id={titleId} className="min-w-0 flex-1 truncate text-sm font-medium">
          {title}
        </h2>

        {/*
          `flex-[3] flex-wrap`, not `shrink-0`. The PDF reader consolidated its
          two toolbars into this slot, so on a 1024px viewport it can run to
          about a thousand pixels with a save cluster showing — `shrink-0`
          would push that straight out of the header. Wrapping drops the
          trailing group to a second line instead, and the trailing group is
          the one that only exists while signing.

          Three shares against the title's one, because the two are competing
          for the same free space and only one of them can truncate gracefully.
          An equal split gave a filename half a header it did not need and left
          the controls wrapping at widths where they fit perfectly well.

          The header itself stays `flex-nowrap`, so the overflow and close
          controls hold the top-right corner and never wrap below the fold.
        */}
        {toolbar === undefined ? null : (
          <div className="flex min-w-0 flex-[3] flex-wrap items-center justify-end gap-1">
            {toolbar}
          </div>
        )}

        <span className="relative flex shrink-0 items-center gap-1">
          {menuItems === undefined ? null : (
            <button
              type="button"
              aria-haspopup="true"
              aria-expanded={menuOpen}
              aria-label={VIEWER_COPY.more(title)}
              onClick={() => {
                setMenuOpen((open) => !open);
              }}
              onBlur={() => {
                // Deferred so a click on the item below lands before the panel
                // unmounts — `RowActions` and `SearchableSelect`'s timing.
                blurTimer.current = setTimeout(() => {
                  setMenuOpen(false);
                }, 120);
              }}
              className={`${FRAME_BUTTON_CLASS} text-secondary hover:text-body`}
            >
              <MoreIcon />
            </button>
          )}

          {menuOpen && menuItems !== undefined ? (
            /*
              A plain absolute panel, and deliberately not a second portal.
              `RowActions` portals only to escape `DataTable`'s `overflow-x-auto`
              scrollport; this header has no such ancestor, so a portal here
              would be machinery for a constraint that does not exist.
            */
            <div
              className="absolute top-full right-0 z-10 mt-1 min-w-[11rem] overflow-hidden rounded-md border py-1"
              style={{
                borderColor: token('bp-border'),
                background: token('bp-panel'),
                boxShadow: token('shadow-md'),
              }}
              onMouseDown={() => {
                clearTimeout(blurTimer.current);
              }}
              onClick={() => {
                setMenuOpen(false);
              }}
            >
              {menuItems}
            </div>
          ) : null}

          {closePlacement === 'end' ? closeButton : null}
        </span>
      </header>

      {children}

      {footer === undefined ? null : (
        <footer
          className="flex shrink-0 items-center justify-between gap-3 border-t px-4 py-3"
          style={{ borderColor: token('bp-border'), background: token('bp-panel') }}
        >
          {footer}
        </footer>
      )}
    </div>,
    document.body,
  );
}

/** Lucide outlines, hand-copied — there is no `lucide-react` in this workspace. */
export function FrameIcon({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/**
 * The step-backward and step-forward chevrons, shared by both readers.
 *
 * They live here rather than in either one because both need them and they are
 * the same two paths: `ImageViewer` steps photographs, `PdfViewer` steps pages.
 * They were `ImageViewer`'s privately until the PDF reader's toolbars were
 * consolidated and it grew page arrows of its own.
 */
export function ChevronLeftIcon() {
  return (
    <FrameIcon>
      <path d="m15 18-6-6 6-6" />
    </FrameIcon>
  );
}

export function ChevronRightIcon() {
  return (
    <FrameIcon>
      <path d="m9 18 6-6-6-6" />
    </FrameIcon>
  );
}

function MoreIcon() {
  return (
    <FrameIcon>
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </FrameIcon>
  );
}

function CloseIcon() {
  return (
    <FrameIcon>
      <path d="M18 6 6 18M6 6l12 12" />
    </FrameIcon>
  );
}
